import * as React from 'react';
import { HTTP as StardogHTTP, query as StardogQuery } from 'stardog';

import { emptyObject } from 'bones/constants/empty';
import { VoiceboxSettingsContextValue } from 'bones/constants/voicebox/settings';
import { runWithRefetchConnectionToken } from 'bones/utils/portal/refetchConnectionToken';
import { useStardogConnectionFromPortalIndex } from 'bones/utils/portal/useStardogConnectionFromPortalIndex';
import { buildStardogQueryRequestOptions } from 'bones/utils/voicebox/stardogQuery';

export type ResultBindings<T extends Record<string, any>> = {
  results?: {
    bindings?: Array<{
      [K in keyof T]:
        | {
            datatype?: string;
            type?: 'uri' | 'literal';
            value?: T[K];
          }
        | undefined;
    }>;
  };
};

export type QueryArgs<Body, Result> = {
  query: string;
  queryResultsMime: StardogHTTP.AcceptMimeType;
  querySchema?: boolean;
  parseBody: (body: Body) => Promise<Result>;
};

export const usePortalVoiceboxStardogQuery = <Args extends any[], Body, Result>(
  connectionIdx: number,
  { databasesById, settings }: VoiceboxSettingsContextValue,
  getQueryArgs: (...fetchArgs: Args) => QueryArgs<Body, Result>,
  overrides?: {
    databaseId?: string;
    requestOptions?: object;
  }
) => {
  const databaseId = overrides?.databaseId || settings.databaseId;
  const stardogConnection = useStardogConnectionFromPortalIndex(connectionIdx);

  const { modelGraphs } = databasesById[databaseId] || {
    modelGraphs: emptyObject,
  };

  return React.useCallback(
    async (...fetchArgs: Args) => {
      if (!stardogConnection || !databaseId) {
        return null;
      }

      const { query, queryResultsMime, querySchema, parseBody } = getQueryArgs(
        ...fetchArgs
      );

      const response = await runWithRefetchConnectionToken(
        stardogConnection,
        connectionIdx,
        (stardogConnection) =>
          StardogQuery.execute(
            stardogConnection,
            databaseId,
            query,
            queryResultsMime,
            overrides?.requestOptions ||
              buildStardogQueryRequestOptions({
                ...settings,
                ...(querySchema
                  ? {
                      namedGraphs: modelGraphs[settings.model] || [],
                      reasoning: false,
                    }
                  : {}),
              })
          )
      );

      if (!response.ok) {
        throw new Error(response.statusText || String(response.status));
      }

      const result = await parseBody(response.body);
      return result;
    },
    [
      connectionIdx,
      databaseId,
      getQueryArgs,
      modelGraphs,
      overrides,
      settings,
      stardogConnection,
    ]
  );
};
